import React from 'react';
import {
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

import THEME from '../../../constants/theme';
import { useIntersectionObserver } from '../../../hooks';
import { useNavigation as useNavigationContext } from '../../../context/NavigationContext';

/**
 * InfoCard component for displaying information with optional image, title, and description
 *
 * @param {Object} props - Component props
 * @param {string} props.title - Card title
 * @param {string} props.description - Card description
 * @param {Array<string>} props.bulletPoints - Array of bullet point strings
 * @param {string} props.imageUrl - URL for card image
 * @param {string} props.imageSrc - Imported image source (use instead of imageUrl)
 * @param {string} props.imageAlt - Alt text for card image
 * @param {string} props.linkTo - Navigation link
 * @param {Function} props.onClick - Click handler function
 * @param {Object} props.buttonProps - Props for action button
 * @param {string} props.variant - Card style variant (default, elevated, outlined)
 * @param {string} props.badge - Optional badge text to display next to arrow
 * @param {Object} props.sx - Additional MUI styling
 * @param {Object} props.imageSx - Additional MUI styling for CardMedia (image)
 * @returns {React.Component} InfoCard component
 */
const InfoCard = ({
  title,
  description,
  bulletPoints = [],
  imageUrl,
  imageSrc,
  imageAlt = 'Card image',
  imageBadge,
  linkTo,
  onClick,
  buttonProps = {},
  variant = 'default',
  badge,
  sx = {},
  imageSx = {},
  hideContent = false // New prop to hide CardContent if needed
}) => {
  // Use intersection observer for scroll animations
  const cardRef = React.useRef(null);
  const isVisible = useIntersectionObserver(cardRef);
  const { navigateToSection } = useNavigationContext();

  // Handle card click
  const handleCardClick = () => {
    if (onClick) {
      onClick();
    } else if (linkTo) {
      // Use navigateToSection with 'hero' to scroll to top of target page
      navigateToSection('hero', linkTo);
    }
  };

  // Determine card styles based on variant
  const getCardStyles = () => {
    const baseStyles = {
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      transition: `transform ${THEME.TRANSITIONS.MEDIUM}, box-shadow ${THEME.TRANSITIONS.MEDIUM}`,
      cursor: linkTo || onClick ? 'pointer' : 'default',
      overflow: 'hidden',
      backgroundColor: THEME.COLORS.NEUTRAL_WHITE
    };

    switch (variant) {
      case 'elevated':
        return {
          ...baseStyles,
          boxShadow: THEME.SHADOWS.MEDIUM,
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: THEME.SHADOWS.LARGE
          }
        };
      case 'outlined':
        return {
          ...baseStyles,
          border: `${THEME.BORDERS.WIDTH.THIN} solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`,
          '&:hover': {
            borderColor: THEME.COLORS.PRIMARY_BLUE
          }
        };
      default:
        return {
          ...baseStyles,
          boxShadow: THEME.SHADOWS.SMALL,
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: THEME.SHADOWS.MEDIUM
          }
        };
    }
  };

  return (
    <Card
      ref={cardRef}
      sx={{
        ...getCardStyles(),
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
        ...sx
      }}
      onClick={handleCardClick}
    >
      {(imageUrl || imageSrc) && (
        <Box sx={{ position: 'relative' }}>
          <CardMedia
            component='img'
            height={imageSx.height || 200}
            image={imageSrc || imageUrl}
            alt={imageAlt}
            sx={{
              objectFit: 'cover', // Maintain aspect ratio, crop if needed
              objectPosition: 'center', // Center the image
              width: '100%',
              ...imageSx
            }}
          />
          {imageBadge && (
            <Box
              sx={{
                position: 'absolute',
                top: THEME.SPACING.SMALL,
                left: THEME.SPACING.SMALL,
                backgroundColor: 'rgba(0, 42, 255, 0.85)',
                color: THEME.COLORS.NEUTRAL_WHITE,
                padding: `${THEME.SPACING.XS} ${THEME.SPACING.SMALL}`,
                borderRadius: THEME.BORDERS.RADIUS.SMALL,
                fontSize: '0.85rem',
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                boxShadow: THEME.SHADOWS.SMALL
              }}
            >
              {imageBadge}
            </Box>
          )}
        </Box>
      )}

      {!hideContent && (
        <CardContent sx={{ flexGrow: 1, padding: THEME.SPACING.LARGE }}>
          {title && (
            <Typography
              variant='h5'
              component='h3'
              sx={{
                fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                color: THEME.COLORS.PRIMARY_BLUE,
                marginBottom: THEME.SPACING.MEDIUM,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY
              }}
            >
              {title}
            </Typography>
          )}

          {description && (
            <Typography
              variant='body1'
              color='text.secondary'
              sx={{
                marginBottom: THEME.SPACING.MEDIUM,
                fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY
              }}
            >
              {description}
            </Typography>
          )}

          {bulletPoints && bulletPoints.length > 0 && (
            <Box
              component='ul'
              sx={{
                mt: 0,
                mb: THEME.SPACING.MEDIUM,
                pl: 2.5,
                listStyleType: 'disc'
              }}
            >
              {bulletPoints.map((point, index) => (
                <Box
                  component='li'
                  key={index}
                  sx={{
                    py: 0.25,
                    fontSize: '0.95rem',
                    color: 'text.secondary',
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                    lineHeight: 1.5
                  }}
                >
                  {point}
                </Box>
              ))}
            </Box>
          )}

          {linkTo && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1
              }}
            >
              {badge && (
                <Typography
                  sx={{
                    fontSize: '1rem',
                    fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.MEDIUM,
                    color: THEME.COLORS.NEUTRAL_GRAY,
                    fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                    fontStyle: 'italic',
                    marginRight: 'auto'
                  }}
                >
                  {badge}
                </Typography>
              )}
              <IconButton
                aria-label='Learn more'
                sx={{
                  color: THEME.COLORS.PRIMARY_BLUE,
                  transition: `transform ${THEME.TRANSITIONS.FAST}, color ${THEME.TRANSITIONS.FAST}`,
                  '&:hover': {
                    color: THEME.COLORS.PRIMARY_BLUE_DARK,
                    backgroundColor: 'transparent',
                    transform: 'translateX(3px)'
                  }
                }}
                {...buttonProps}
              >
                <ArrowForwardIcon />
              </IconButton>
            </Box>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default InfoCard;
